import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { IWorkbookDataFreshnessInfo } from '~src/dataAcceleration/models/IDataFreshnessInfo';
import { UpdateDataFreshnessPolicyRequest } from '~src/dataAcceleration/serverApi/requests/UpdateDataFreshnessPolicyRequest';
import { ExtractFailureNotificationOptInRequest, ExtractFailureNotificationOptOutRequest } from '~src/extractTask/serverApi/requests/ExtractFailureNotificationRequest';
import { WebClientDetailFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '~src/httpClient/fetchers/resourceFetchers/WebClientListResourceFetcher';
import { ITaggableContent } from '~src/tags/ITaggableContent';

import { UnencryptedConnectionKeychain } from '../../connectivity/serverApi/types/connection/IConnectionKeyChain';
import { IDataFreshnessPolicyFetcher } from '../../dataAcceleration/resources/resourceFetchers/IDataFreshnessPolicyFetcher';
import { DisableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/DisableWorkbookAccelerationRequest';
import { EnableWorkbookAccelerationRequest } from '../../dataAcceleration/serverApi/requests/EnableWorkbookAccelerationRequest';
import { CancelExtractCreationRequest } from '../../extractTask/serverApi/requests/CancelExtractCreationRequest';
import { CreateExtractTasksRequest } from '../../extractTask/serverApi/requests/CreateExtractTasksRequest';
import { GetExtractDraftIdRequest } from '../../extractTask/serverApi/requests/GetExtractDraftIdRequest';
import { ExtractTaskTargetType } from '../../extractTask/serverApi/types/IExtractTask';
import { IDetailFetcher } from '../../httpClient/fetchers/IDetailFetcher';
import { IListFetcher } from '../../httpClient/fetchers/IListFetcher';
import { FieldValuesQuery } from '../../httpClient/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../httpClient/resources/Resource';
import { ResourceQuery } from '../../httpClient/resources/ResourceQuery';
import { GetFieldValuesRequest } from '../../httpClient/serverApi/requests/GetFieldValuesRequest';
import { IFieldValue } from '../../httpClient/serverApi/types/IFieldValue';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../recents/requests/MarkRecentlyViewedRequest';
import { WorkbookContentType } from '../../spacesPlatform/models/ContentTypes';
import {IWorkbookDetailInfo, IWorkbookInfo } from '../models/IWorkbookInfo';
import { WorkbookMapper } from '../models/mappers/WorkbookMapper';
import { AddTagsToWorkbooksRequest } from '../ServerApi/requests/AddTagsToWorkbooksRequest';
import { DeleteWorkbooksRequest } from '../ServerApi/requests/DeleteWorkbooksRequest';
import { SetDisplayTabsRequest } from '../ServerApi/requests/displayTabs/SetDisplayTabsRequest';
import { RunCreateExtractsOnWorkbooksRequest } from '../ServerApi/requests/extractRefresh/RunCreateExtractsOnWorkbooksRequest';
import { RunExtractRefreshesOnWorkbooksRequest } from '../ServerApi/requests/extractRefresh/RunExtractRefreshesOnWorkbooksRequest';
import { GetWorkbookActionsRequest } from '../ServerApi/requests/GetWorkbookActionsRequest';
import { GetWorkbooksRequest } from '../ServerApi/requests/GetWorkbooksRequest';
import {
  IIsWorkbookServerRefreshableRequest,
  IIsWorkbookServerRefreshableResult,
} from '../ServerApi/requests/IsWorkbookServerRefreshable';
import { MoveWorkbooksToProjectRequest } from '../ServerApi/requests/MoveWorkbooksToProjectRequest';
import { PerformExtractEncryptionActionOnWorkbooksRequest } from '../ServerApi/requests/PerformExtractEncryptionActionOnWorkbooksRequest';
import { RemoveTagsFromWorkbooksRequest } from '../ServerApi/requests/RemoveTagsFromWorkbooksRequest';
import { RenameWorkbookRequest } from '../ServerApi/requests/RenameWorkbookRequest';
import { IRevertWorkbookResult, RevertWorkbookRequest } from '../ServerApi/requests/RevertWorkbookRequest';
import { SetWorkbookDescriptionRequest } from '../ServerApi/requests/SetWorkbookDescriptionRequest';
import { SetWorkbooksOwnerRequest } from '../ServerApi/requests/SetWorkbooksOwnerRequest';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';

export class WorkbooksResource implements IListResource<IWorkbookInfo>, IFieldValuesResource, IDetailResource<IWorkbookDetailInfo>, IActionResource<IWorkbookActions>, ITaggableContent {

  private listFetcher: IListFetcher<IWorkbookInfo>;
  private allQuery: ResourceQuery<IWorkbookInfo>;
  private detailFetcher: IDetailFetcher<IWorkbookDetailInfo>;
  private dataFreshnessPolicyFetcher: IDataFreshnessPolicyFetcher<IWorkbookDataFreshnessInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor() {
    this.listFetcher = new WebClientListResourceFetcher(GetWorkbooksRequest, ServerApiMapper.workbooks);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = new WebClientDetailFetcher('getWorkbook', WorkbookMapper.mapDetail);
    this.dataFreshnessPolicyFetcher = new WebClientDetailFetcher('getWorkbookDataFreshnessPolicy', WorkbookMapper.mapDataFreshnessPolicy);
    this.fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public isWorkbookInfo(item: any): item is IWorkbookInfo {
    return item &&
           item.contentType &&
           item.contentType === 'workbook' &&
           item.hasOwnProperty('numSheets'); // this field exists in IWorkbookInfo and not IWorkbookDetailInfo
  }

  public isWorkbookDetailInfo(item: any): item is IWorkbookDetailInfo {
    return item &&
      item.contentType &&
      item.contentType === 'workbook' &&
      item.hasOwnProperty('description'); // this field exists in IWorkbookDetailInfo and not IWorkbookInfo
  }

  public all(): ResourceQuery<IWorkbookInfo> {
    return this.allQuery;
  }

  public getById(id: string): Promise<IWorkbookDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getDataFreshnessPolicyById(workbookId: string): Promise<IWorkbookDataFreshnessInfo> {
    return this.dataFreshnessPolicyFetcher.fetchById(workbookId);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'workbook');
  }

  // Actions

  public actions(id: string): Promise<IWorkbookActions> {
    return WebHttpClient.sendRequest(new GetWorkbookActionsRequest(id));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteWorkbooksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public rename(id: string, name: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new RenameWorkbookRequest(id, name));
  }

  public setDescription(id: string, description: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetWorkbookDescriptionRequest(id, description));
  }

  public setOwner(ids: string[], ownerId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetWorkbooksOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, workbookIds: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(workbookIds,
        (idsBlock) => new MoveWorkbooksToProjectRequest({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public setDisplayTabs(ids: string[], displayTabs: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new SetDisplayTabsRequest({
          ids: idsBlock,
          displayTabs: displayTabs
        })),
      ResultHelper.combineResults);
  }

  public updateDataFreshnessPolicy(workbookId: string,
      deletePolicy: boolean, intervalInMinutes: number,
      cachingPolicyType: string, daysStringCsv: string, refreshTimeOfDay: number, timezoneId: string): Promise<IResult> {
    const request = new UpdateDataFreshnessPolicyRequest({
      workbookId: workbookId,
      deletePolicy: deletePolicy,
      intervalInMinutes: intervalInMinutes,
      cachingPolicyType: cachingPolicyType,
      daysStringCsv: daysStringCsv,
      refreshTimeOfDay: refreshTimeOfDay,
      timezoneId: timezoneId
    });
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(request);
  }

  public addTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new AddTagsToWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public removeTags(ids: string[], tags: string[]): Promise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests2(ids, tags,
          (idsBlock, tagsBlock) => new RemoveTagsFromWorkbooksRequest(idsBlock, tagsBlock)),
        ResultHelper.combineResults)
      : Promise.resolve({});
  }

  public revertWorkbook(id: string, version: number, keychains?: UnencryptedConnectionKeychain[], encryptedPasswordKeyId?: string): Promise<IRevertWorkbookResult> {
    const request = new RevertWorkbookRequest({
      id: id,
      version: version,
      keychains: keychains,
      encryptedPasswordKeyId: encryptedPasswordKeyId
    });
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(request);
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): Promise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd));
  }

  public createExtractTask(ids: string[], scheduleId: string, extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new CreateExtractTasksRequest({
          targetType: ExtractTaskTargetType.Workbook,
          targetIds: idsBlock,
          scheduleId: scheduleId,
          type: extractType // ExtractTaskType
        })),
      ResultHelper.combineResults);
  }

  public enableWorkbookAcceleration(ids: string[], scheduleId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new EnableWorkbookAccelerationRequest({
          targetIds: idsBlock,
          scheduleId: scheduleId
        })),
      ResultHelper.combineResults);
  }

  public disableWorkbookAcceleration(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids,
        (idsBlock) => new DisableWorkbookAccelerationRequest({
          targetIds: idsBlock
        })),
      ResultHelper.combineResults);
  }

  public refreshExtracts(ids: string[], extractType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) =>
          new RunExtractRefreshesOnWorkbooksRequest(idsBlock, extractType)
      ),
      ResultHelper.combineResults);
  }

  public createExtracts(ids: string[], dsIds: string[], encrypt: boolean): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
      new RunCreateExtractsOnWorkbooksRequest(idsBlock, dsIds, encrypt)
      ),
      ResultHelper.combineResults);
  }

  public encryptWorkbooks(ids: string[], actionType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new PerformExtractEncryptionActionOnWorkbooksRequest(idsBlock, actionType)
      ),
      ResultHelper.combineResults);
  }

  public enableFailureNotification(id: string, contentType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new ExtractFailureNotificationOptInRequest(id, contentType)
    );
  }

  public disableFailureNotification(id: string, contentType: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new ExtractFailureNotificationOptOutRequest(id, contentType)
    );
  }

  public markRecentlyViewed(id: string) {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.Workbook
    }));
  }

  public isWorkbookServerRefreshable(id: string): Promise<IIsWorkbookServerRefreshableResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new IIsWorkbookServerRefreshableRequest({ id: id }));
  }

  public cancelExtractCreationOnWorkbooks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) =>
        new CancelExtractCreationRequest(ids, WorkbookContentType.name)
      ),
    ResultHelper.combineResults);
  }

  public getExtractDraftId(workbookId: string): Promise<IResult> {
    return WebHttpClient.sendRequest(new GetExtractDraftIdRequest(workbookId));
  }
}

export const Workbooks = new WorkbooksResource();
